/**
 * EntityLinkerModal Component
 * 
 * A reusable modal component for creating bidirectional links between entities.
 * Supports linking any entity type (Days, Accommodations, Places, Travel, Documents)
 * to any other entity type.
 * 
 * Features:
 * - Hierarchical entity browser
 * - Filters out already-linked entities
 * - Multi-select capability
 * - Visual indicators for existing links
 * - Relationship-aware suggestions
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTrip } from '../contexts/TripContext';
import { entityLinkingService } from '../services/entityLinkingService';
import { daysService } from '@trip-planner-monorepo/days';
import { accommodationService } from '@trip-planner-monorepo/accommodation';
import { placesService } from '@trip-planner-monorepo/places';
import { travelService } from '@trip-planner-monorepo/travel';
import { documentService } from '@trip-planner-monorepo/documents';
import { FaChevronDown, FaChevronRight, FaCalendarAlt, FaHotel, FaPlane, FaMapMarkerAlt, FaFileAlt, FaLink } from 'react-icons/fa';
import './EntityLinkerModal.css';

function EntityLinkerModal({
  sourceEntity,
  onLinkCreated,
  onClose,
  excludeEntityTypes = [],
  allowMultiSelect = true,
  showExistingLinks = false
}) {
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [entities, setEntities] = useState({
    days: [],
    accommodations: [],
    places: [],
    travel: [],
    documents: []
  });
  const [existingLinks, setExistingLinks] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    days: false,
    accommodations: false,
    places: false,
    travel: false,
    documents: false
  });

  const { currentUser } = useAuth();
  const { effectiveUserId } = useTrip();
  const { tripId } = useParams();

  /**
   * Formats date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  /**
   * Fetches all entities and filters out already linked ones
   */
  const fetchEntities = async () => {
    if (!currentUser || !tripId || !sourceEntity) return;

    setLoading(true);
    setError('');

    try {
      // Get linkable entities (already filters out linked ones)
      const targetEntityTypes = ['day', 'accommodation', 'place', 'travel', 'document']
        .filter(type => !excludeEntityTypes.includes(type));

      const linkableEntities = await entityLinkingService.getLinkableEntities(
        effectiveUserId,
        tripId,
        sourceEntity.type,
        sourceEntity.id,
        targetEntityTypes
      );

      // Also get existing links to show them if requested
      const links = await entityLinkingService.getEntityLinks(
        effectiveUserId,
        tripId,
        sourceEntity.type,
        sourceEntity.id
      );

      const existingLinksSet = new Set();
      links.forEach(link => {
        existingLinksSet.add(`${link.type}-${link.id}`);
      });
      setExistingLinks(existingLinksSet);

      // If showExistingLinks is true, merge linkable and existing
      if (showExistingLinks) {
        // Fetch all entities to show existing links too
        const [allDays, allAccommodations, allPlaces, allTravel, allDocuments] = await Promise.all([
          daysService.fetchDays(effectiveUserId, tripId),
          accommodationService.fetchAccommodation(effectiveUserId, tripId),
          placesService.fetchPlaces(effectiveUserId, tripId),
          travelService.fetchTravel(effectiveUserId, tripId),
          documentService.fetchDocuments(effectiveUserId, tripId)
        ]);

        setEntities({
          days: allDays || [],
          accommodations: allAccommodations || [],
          places: allPlaces || [],
          travel: allTravel || [],
          documents: allDocuments || []
        });
      } else {
        setEntities({
          days: linkableEntities.day || [],
          accommodations: linkableEntities.accommodation || [],
          places: linkableEntities.place || [],
          travel: linkableEntities.travel || [],
          documents: linkableEntities.document || []
        });
      }

      // Auto-expand sections that have entities
      const sectionsToExpand = {};
      if (linkableEntities.day?.length > 0) sectionsToExpand.days = true;
      if (linkableEntities.accommodation?.length > 0) sectionsToExpand.accommodations = true;
      if (linkableEntities.place?.length > 0) sectionsToExpand.places = true;
      if (linkableEntities.travel?.length > 0) sectionsToExpand.travel = true;
      if (linkableEntities.document?.length > 0) sectionsToExpand.documents = true;

      setExpandedSections(prev => ({ ...prev, ...sectionsToExpand }));
    } catch (err) {
      console.error('Error fetching entities:', err);
      setError('Failed to load entities. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggles section expansion
   */
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  /**
   * Handles entity selection/deselection
   */
  const toggleEntitySelection = (entityType, entityId, entityName) => {
    const key = `${entityType}-${entityId}`;

    // Check if already linked
    if (existingLinks.has(key) && !showExistingLinks) {
      return; // Don't allow selection of already linked entities
    }

    setSelectedEntities(prev => {
      const existingIndex = prev.findIndex(
        item => item.entityType === entityType && item.entityId === entityId
      );

      if (existingIndex >= 0) {
        return prev.filter((_, index) => index !== existingIndex);
      } else {
        if (!allowMultiSelect && prev.length > 0) {
          // Single select mode - replace selection
          return [{
            entityType,
            entityId,
            entityName,
            selectedAt: new Date().toISOString()
          }];
        }
        return [...prev, {
          entityType,
          entityId,
          entityName,
          selectedAt: new Date().toISOString()
        }];
      }
    });
  };

  /**
   * Checks if an entity is selected
   */
  const isEntitySelected = (entityType, entityId) => {
    return selectedEntities.some(
      item => item.entityType === entityType && item.entityId === entityId
    );
  };

  /**
   * Checks if an entity is already linked
   */
  const isEntityLinked = (entityType, entityId) => {
    return existingLinks.has(`${entityType}-${entityId}`);
  };

  /**
   * Handles creating links
   */
  const handleCreateLinks = async () => {
    if (selectedEntities.length === 0) {
      setError('Please select at least one entity to link.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const results = [];
      const errors = [];

      // Create links for each selected entity
      for (const selected of selectedEntities) {
        try {
          // Skip if already linked
          if (existingLinks.has(`${selected.entityType}-${selected.entityId}`)) {
            continue;
          }

          const result = await entityLinkingService.createLink(
            effectiveUserId,
            tripId,
            sourceEntity.type,
            sourceEntity.id,
            selected.entityType,
            selected.entityId,
            {
              bidirectional: true,
              metadata: {
                entityName: selected.entityName
              }
            }
          );

          results.push({
            ...selected,
            success: true,
            ...result
          });
        } catch (err) {
          console.error(`Error linking to ${selected.entityType}/${selected.entityId}:`, err);
          errors.push({
            ...selected,
            error: err.message
          });
        }
      }

      if (results.length > 0) {
        if (onLinkCreated) {
          onLinkCreated(results);
        }
        onClose();
      } else if (errors.length > 0) {
        setError(`Failed to create ${errors.length} link(s). ${errors[0].error}`);
      }
    } catch (err) {
      console.error('Error creating links:', err);
      setError('Failed to create links. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Gets the correct entity ID for an entity
   * Documents use firestoreId, others use id
   */
  const getEntityId = (entity, entityType) => {
    if (entityType === 'document') {
      // Documents may have firestoreId or id
      return entity.firestoreId || entity.id;
    }
    return entity.id;
  };

  /**
   * Renders entity items for a section
   */
  const renderEntityItems = (entityType, entitiesList, getDisplayName, getIcon) => {
    if (!entitiesList || entitiesList.length === 0) {
      return (
        <div className="entity-linker-empty">
          <span>No {entityType} found</span>
        </div>
      );
    }

    return entitiesList.map((entity, index) => {
      const entityId = getEntityId(entity, entityType);
      const displayName = getDisplayName(entity, index);
      const isSelected = isEntitySelected(entityType, entityId);
      const isLinked = isEntityLinked(entityType, entityId);

      // Use a unique key combining entityType, entityId, and index to prevent React key conflicts
      const uniqueKey = `${entityType}-${entityId}-${index}`;

      return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          key={uniqueKey}
          className={`entity-linker-item ${isSelected ? 'selected' : ''} ${isLinked ? 'already-linked' : ''}`}
          onClick={(e) => {
            e.stopPropagation(); // Prevent event bubbling
            if (!isLinked) {
              toggleEntitySelection(entityType, entityId, displayName);
            }
          }}
          title={isLinked ? 'Already linked' : undefined}
        >
          <input
            type={allowMultiSelect ? 'checkbox' : 'radio'}
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation(); // Prevent event bubbling
              if (!isLinked) {
                toggleEntitySelection(entityType, entityId, displayName);
              }
            }}
            onClick={(e) => e.stopPropagation()} // Prevent double-firing
            className="entity-linker-checkbox"
            disabled={isLinked}
          />
          <span className="entity-linker-icon">{getIcon(entity)}</span>
          <span className="entity-linker-name">
            {displayName}
            {isLinked && <span className="already-linked-indicator"> (Already Linked)</span>}
          </span>
        </div>
      );
    });
  };

  // Fetch entities when component mounts
  useEffect(() => {
    fetchEntities();
  }, [currentUser, tripId, sourceEntity]);

  if (!sourceEntity) {
    return null;
  }

  const getEntityTypeDisplayName = (type) => {
    const names = {
      'day': 'Day',
      'accommodation': 'Accommodation',
      'place': 'Place',
      'travel': 'Travel',
      'document': 'Document'
    };
    return names[type] || type;
  };

  const dialogLabel = `Link ${getEntityTypeDisplayName(sourceEntity.type)}`;

  return (
    <div className="entity-linker-modal-overlay">
      <div
        className="entity-linker-modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={dialogLabel}
      >
        <button className="entity-linker-modal-close" onClick={onClose} title="Close" aria-label="Close">&times;</button>

        <div className="entity-linker-modal-header">
          <h2>
            <FaLink className="header-icon" />
            Link {getEntityTypeDisplayName(sourceEntity.type)}
          </h2>
          <p>Link &quot;{sourceEntity.name}&quot; to other trip items</p>
        </div>

        <div className="entity-linker-content">
          {error && (
            <div className="entity-linker-error" style={{ color: 'red', marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          {loading ? (
            <div className="entity-linker-loading">
              <div className="spinner"></div>
              Loading entities...
            </div>
          ) : (
            <div className="entity-linker-wrapper">
              <div className="hierarchical-association">
                {/* Days Section */}
                {!excludeEntityTypes.includes('day') && (
                  <div className="association-section">
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                    <div
                      className="section-header"
                      onClick={() => toggleSection('days')}
                    >
                      <span className="expand-icon">
                        {expandedSections.days ? <FaChevronDown /> : <FaChevronRight />}
                      </span>
                      <FaCalendarAlt className="section-icon" />
                      <span className="section-title">Days ({entities.days.length})</span>
                    </div>

                    {expandedSections.days && (
                      <div className="section-content">
                        {renderEntityItems(
                          'day',
                          entities.days,
                          (day, index) => `Day ${day.order + 1} - ${formatDate(day.date)}`,
                          () => <FaCalendarAlt />
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Accommodations Section */}
                {!excludeEntityTypes.includes('accommodation') && (
                  <div className="association-section">
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                    <div
                      className="section-header"
                      onClick={() => toggleSection('accommodations')}
                    >
                      <span className="expand-icon">
                        {expandedSections.accommodations ? <FaChevronDown /> : <FaChevronRight />}
                      </span>
                      <FaHotel className="section-icon" />
                      <span className="section-title">Accommodations ({entities.accommodations.length})</span>
                    </div>

                    {expandedSections.accommodations && (
                      <div className="section-content">
                        {renderEntityItems(
                          'accommodation',
                          entities.accommodations,
                          (acc, index) => `${acc.summary || acc.details?.name || 'Unnamed'} - ${acc.details?.city || ''}`,
                          () => <FaHotel />
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Places Section */}
                {!excludeEntityTypes.includes('place') && (
                  <div className="association-section">
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                    <div
                      className="section-header"
                      onClick={() => toggleSection('places')}
                    >
                      <span className="expand-icon">
                        {expandedSections.places ? <FaChevronDown /> : <FaChevronRight />}
                      </span>
                      <FaMapMarkerAlt className="section-icon" />
                      <span className="section-title">Places ({entities.places.length})</span>
                    </div>

                    {expandedSections.places && (
                      <div className="section-content">
                        {renderEntityItems(
                          'place',
                          entities.places,
                          (place, index) => place.name || 'Unnamed Place',
                          () => <FaMapMarkerAlt />
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Travel Section */}
                {!excludeEntityTypes.includes('travel') && (
                  <div className="association-section">
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                    <div
                      className="section-header"
                      onClick={() => toggleSection('travel')}
                    >
                      <span className="expand-icon">
                        {expandedSections.travel ? <FaChevronDown /> : <FaChevronRight />}
                      </span>
                      <FaPlane className="section-icon" />
                      <span className="section-title">Travel ({entities.travel.length})</span>
                    </div>

                    {expandedSections.travel && (
                      <div className="section-content">
                        {renderEntityItems(
                          'travel',
                          entities.travel,
                          (travel, index) => `${travel.summary || travel.details?.type || 'Travel'} - ${travel.details?.from || ''} to ${travel.details?.to || ''}`,
                          () => <FaPlane />
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Documents Section */}
                {!excludeEntityTypes.includes('document') && (
                  <div className="association-section">
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                    <div
                      className="section-header"
                      onClick={() => toggleSection('documents')}
                    >
                      <span className="expand-icon">
                        {expandedSections.documents ? <FaChevronDown /> : <FaChevronRight />}
                      </span>
                      <FaFileAlt className="section-icon" />
                      <span className="section-title">Documents ({entities.documents.length})</span>
                    </div>

                    {expandedSections.documents && (
                      <div className="section-content">
                        {renderEntityItems(
                          'document',
                          entities.documents,
                          (doc, index) => doc.title || doc.name || 'Untitled Document',
                          () => <FaFileAlt />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="entity-linker-actions">
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleCreateLinks}
            disabled={loading || selectedEntities.length === 0}
            title={selectedEntities.length === 0 ? 'Please select at least one entity to link' : undefined}
          >
            {loading ? 'Creating Links...' : `Create ${selectedEntities.length > 0 ? `${selectedEntities.length} ` : ''}Link${selectedEntities.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EntityLinkerModal;
